/**
 * Order Table Helper Functions
 * Utilities for column filtering, styling, and data processing
 */

import React from 'react';
import type { OrderTableColumn } from '../types/components';
import type { OrderSummary } from '../utils/types';
import { formatNumberPlain } from './formatNumbers';

/**
 * Get nested value from object using dot notation path
 * @param obj - Object to extract value from
 * @param path - Dot notation path (e.g., 'metadata.glass_side_one_value_id')
 * @returns The nested value or undefined
 */
export const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Check if a value should be considered empty for column hiding purposes
 * @param value - Value to check
 * @returns True if value is considered empty
 */
export const isEmptyValue = (value: any): boolean => {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    value === '-' ||  // Add dash as empty value ( commonly used placeholder)
    (typeof value === 'string' && value.trim() === '') ||
    (typeof value === 'string' && value.trim() === '0') || // String zero
    (typeof value === 'boolean' && value === false) ||
    (typeof value === 'number' && isNaN(value))  // Only NaN numbers, not zero (zero might be valid)
  );
};

/**
 * Column visibility for Image / Image Type / View — show only when this order has S3 images or Woo drawing URLs.
 * Aligns with OrderImagesCells and backend OrderSummarySerializer.get_has_images / ProductionLineOrderSerializer.
 */
export const orderHasImagesColumnData = (order: OrderSummary): boolean => {
  if (order.has_images === true) {
    return true;
  }
  const meta = (order as Record<string, unknown>).metadata as Record<string, unknown> | undefined;
  const raw = meta?.woo_drawings;
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? [raw]
      : [];
  return list.some(
    (d) => typeof d === 'object' && d !== null && !!(d as { url?: string }).url
  );
};

/**
 * Determine which columns should be visible based on data availability
 * @param orders - Array of orders to analyze
 * @param allColumns - All available column definitions
 * @returns Array of columns that should be displayed
 */
export const getVisibleColumns = (
  orders: OrderSummary[], 
  allColumns: OrderTableColumn[]
): OrderTableColumn[] => {
  if (!orders || orders.length === 0) {
    // If no orders, show only basic columns and sticky columns
    return allColumns.filter(column => 
      column.sticky ||
      column.group === 'basic' ||
      column.condition?.alwaysVisible ||
      !column.condition?.hideIfEmpty
    );
  }

  const filteredColumns = allColumns.filter(column => {
    // ALWAYS show sticky columns - they must never be filtered out
    if (column.sticky) {
      return true;
    }

    // For auto-hide feature, check all columns unless explicitly excluded
    // Only skip columns that are marked as always visible
    if (column.condition?.alwaysVisible) {
      return true;
    }

    // Check if any order has data for this column
    const hasData = column.condition?.hasDataCheck
      ? orders.some(order => column.condition!.hasDataCheck!(order))
      : orders.some(order => {
          const value = getNestedValue(order, column.key);
          const isEmpty = isEmptyValue(value);
          return !isEmpty;
        });

    // Always show basic essential columns even if empty (excluding image columns - they use hideIfEmpty)
    const essentialGroups = ['job_context', 'basic', 'system'];
    const essentialColumns = ['customer_name', 'website_job_number', 'product_type', 'quantity', 'manufacture_date'];
    
    const isEssential = essentialGroups.includes(column.group || '') || essentialColumns.includes(column.key);
    const shouldShow = isEssential ? (hasData || essentialColumns.includes(column.key)) : hasData;
    
    return shouldShow;
  });

  // CRITICAL: Ensure sticky columns are always at the beginning in correct order
  const stickyColumns = filteredColumns.filter(col => col.sticky);
  const nonStickyColumns = filteredColumns.filter(col => !col.sticky);
  
  return [...stickyColumns, ...nonStickyColumns];
};

/**
 * Apply conditional styles to a table cell
 * @param value - Cell value
 * @param order - Full order object for context
 * @param column - Column definition with style conditions
 * @param glassValuesLookup - Optional lookup map for glass values
 * @returns Object with className and inline styles
 */
export const applyCellStyles = (
  value: any, 
  order: OrderSummary, 
  column: OrderTableColumn,
  glassValuesLookup?: Map<string, { is_arrival: boolean; is_cutting: boolean }>
): { className: string; style: React.CSSProperties } => {
  const styles = column.condition?.styles;
  if (!styles) {
    return { className: '', style: {} };
  }

  const classNames: string[] = [];
  const inlineStyles: React.CSSProperties = {};

  // Apply background color - use inline style for production_line columns to prevent row override
  if (styles.backgroundColor) {
    // For glass value columns, pass the lookup if available
    const bgColor = styles.backgroundColor(value, order, glassValuesLookup);
    if (bgColor) {
      // For production_line group columns, use inline styles to prevent row background override
      if (column.group === 'production_line') {
        // Convert Tailwind class to inline style
        const colorMap: Record<string, string> = {
          'bg-white': '#ffffff',
          'bg-gray-300': '#d1d5db',
          'bg-red-200': '#fecaca',
          'bg-green-200': '#bbf7d0',
        };
        inlineStyles.backgroundColor = colorMap[bgColor] || bgColor;
      } else {
        classNames.push(bgColor);
      }
    }
  }

  // Apply text color
  if (styles.textColor) {
    const textColor = styles.textColor(value, order);
    if (textColor) classNames.push(textColor);
  }

  // Apply font size
  if (styles.fontSize) {
    const fontSize = styles.fontSize(value, order);
    if (fontSize) classNames.push(fontSize);
  }

  return { 
    className: classNames.join(' '), 
    style: inlineStyles 
  };
};

/**
 * Check if a cell should be hidden based on conditional logic
 * @param value - Cell value
 * @param order - Full order object for context
 * @param column - Column definition
 * @returns True if cell should be hidden
 */
export const shouldHideCell = (
  value: any, 
  order: OrderSummary, 
  column: OrderTableColumn
): boolean => {
  return column.condition?.styles?.hidden?.(value, order) || false;
};

/**
 * Format a cell value based on its column type
 * @param value - Raw value from order object
 * @param column - Column definition
 * @param order - Full order object for context
 * @returns Formatted value for display
 */
export const formatCellValue = (
  value: any,
  column: OrderTableColumn,
  order: OrderSummary
): React.ReactNode => {
  // Use custom render function if provided
  if (column.render) {
    return column.render(value, order);
  }

  // Handle empty values
  if (isEmptyValue(value)) {
    return '-';
  }

  // Format based on column type
  switch (column.type) {
    case 'boolean':
      return value ? 'Yes' : 'No';
    
    case 'number':
      return typeof value === 'number' ? formatNumberPlain(value) : value;
    
    case 'currency':
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      return isNaN(numValue) ? '-' : `£${numValue.toFixed(2)}`;
    
    case 'date':
      try {
        return new Date(value).toLocaleDateString();
      } catch {
        return value;
      }
    
    case 'text':
    default:
      return String(value);
  }
};

/**
 * Calculate table column widths based on content and configuration
 * @param columns - Visible columns
 * @param containerWidth - Available container width
 * @returns Object mapping column keys to calculated widths
 */
export const calculateColumnWidths = (
  columns: OrderTableColumn[],
  containerWidth: number
): Record<string, string> => {
  const fixedWidthColumns = columns.filter(col => col.width);
  const flexColumns = columns.filter(col => !col.width);
  
  // Calculate used width by fixed columns
  const fixedWidth = fixedWidthColumns.reduce((total, col) => {
    const width = parseInt(col.width || '0', 10);
    return total + width;
  }, 0);

  // Distribute remaining width among flex columns
  const remainingWidth = Math.max(containerWidth - fixedWidth, 0);
  const flexColumnWidth = flexColumns.length > 0 
    ? Math.floor(remainingWidth / flexColumns.length)
    : 0;

  const widths: Record<string, string> = {};
  
  columns.forEach(col => {
    if (col.width) {
      widths[col.key] = col.width;
    } else {
      widths[col.key] = `${Math.max(flexColumnWidth, 100)}px`;
    }
  });

  return widths;
};

/**
 * Group columns by their group property for organized display
 * @param columns - Columns to group
 * @returns Object mapping group names to columns
 */
export const groupColumnsByGroup = (
  columns: OrderTableColumn[]
): Record<string, OrderTableColumn[]> => {
  return columns.reduce((groups, column) => {
    const group = column.group || 'default';
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(column);
    return groups;
  }, {} as Record<string, OrderTableColumn[]>);
};

/**
 * Filter columns for responsive display based on screen size
 * @param columns - All available columns
 * @param screenSize - Current screen size category
 * @returns Filtered columns appropriate for screen size
 */
export const getResponsiveColumns = (
  columns: OrderTableColumn[],
  screenSize: 'mobile' | 'tablet' | 'desktop'
): OrderTableColumn[] => {
  // Priority groups for different screen sizes
  const priorities: Record<string, string[]> = {
    mobile: ['basic'],
    tablet: ['basic', 'glass_specs', 'single_glazed', 'double_glazed'],
    desktop: [] // All columns
  };

  const allowedGroups = priorities[screenSize];
  
  if (allowedGroups.length === 0) {
    return columns; // Desktop shows all
  }

  return columns.filter(column => 
    allowedGroups.includes(column.group || 'basic')
  );
};

/**
 * Validate column configuration for potential issues
 * @param columns - Column definitions to validate
 * @returns Array of validation errors
 */
export const validateColumnConfig = (
  columns: OrderTableColumn[]
): string[] => {
  const errors: string[] = [];

  // Check for duplicate keys
  const keys = columns.map(col => col.key);
  const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);
  if (duplicates.length > 0) {
    errors.push(`Duplicate column keys found: ${duplicates.join(', ')}`);
  }

  // Check for invalid column types
  const validTypes = ['text', 'number', 'boolean', 'currency', 'date', 'component'];
  columns.forEach(col => {
    if (!validTypes.includes(col.type)) {
      errors.push(`Invalid column type '${col.type}' for column '${col.key}'`);
    }
  });

  // Check for render function with non-component type
  columns.forEach(col => {
    if (col.render && col.type !== 'component') {
      console.warn(`Column '${col.key}' has render function but type is not 'component'`);
    }
  });

  return errors;
};

/**
 * Boolean flag codes for rendering compact column values
 */
export const BOOLEAN_FLAG_CODES: Record<string, string> = {
  'polished_edges': 'POL',
  'dubbed_corners': 'DUB',
  'aluminium_backing_foil': 'ALF',
  'safety_backing': 'SB',
  'argon_filled': 'ARG',
  'decorative_lead_soldering_required': 'SOL'
};

/**
 * Background colors for boolean flags
 */
export const BOOLEAN_FLAG_COLORS: Record<string, string> = {
  'POL': 'bg-cyan-100',
  'DUB': 'bg-purple-100',
  'ALF': 'bg-slate-200',
  'SB': 'bg-rose-100',
  'ARG': 'bg-green-100',
  'SOL': 'bg-amber-100'
};

/**
 * Render boolean value as a flag code with background color
 * @param value - Boolean value
 * @param flagCode - Flag code to display (e.g., 'POL', 'DUB')
 * @returns Flag code in bold with colored background if true, '-' if false
 */
export const renderBooleanFlag = (value: any, flagCode: string): React.ReactNode => {
  if (!value) return '-';
  const bgColor = BOOLEAN_FLAG_COLORS[flagCode] || 'bg-gray-100';
  return React.createElement('span', { className: `font-bold ${bgColor} px-2 py-1 rounded` }, flagCode);
};

/**
 * Check if a glass value is marked as arrival
 * @param order - Order object
 * @param columnKey - Column key (e.g., 'metadata.glass_side_one_value_id')
 * @param glassValueId - Glass value ID from the cell value
 * @param glassValuesLookup - Optional lookup map of glass value ID to { is_arrival: boolean }
 * @returns True if the glass value is arrival, false otherwise
 */
export const isGlassValueArrival = (
  order: OrderSummary,
  columnKey: string,
  glassValueId: string | null | undefined,
  glassValuesLookup?: Map<string, { is_arrival: boolean; is_cutting: boolean }>
): boolean => {
  // If no glass value ID, return false
  if (!glassValueId) {
    return false;
  }

  // Check for direct is_arrival fields (Production Line orders)
  if (columnKey.includes('glass_side_one_value_id')) {
    if ((order as any).glass_side_one_is_arrival !== undefined) {
      return (order as any).glass_side_one_is_arrival === true;
    }
  } else if (columnKey.includes('glass_side_two_value_id')) {
    if ((order as any).glass_side_two_is_arrival !== undefined) {
      return (order as any).glass_side_two_is_arrival === true;
    }
  } else if (columnKey.includes('glass_side_three_value_id') || columnKey.includes('middle_glass_panel_value_id')) {
    if ((order as any).middle_glass_panel_is_arrival !== undefined) {
      return (order as any).middle_glass_panel_is_arrival === true;
    }
  }

  // If direct fields not available, use lookup map if provided
  if (glassValuesLookup) {
    const glassValue = glassValuesLookup.get(glassValueId);
    return glassValue?.is_arrival === true;
  }

  // Default to false if no lookup available
  return false;
};